/**
 * AvatarEditor - Component for customizing avatar appearance
 * 
 * Used in:
 * - Post-registration onboarding
 * - Profile/account page
 */

import { useState, useEffect } from 'react';
import { Shuffle, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import AvatarCanvas from './AvatarCanvas';
import { playClick } from '@/utils/sounds';
import { UNIFORM_COLOR_PALETTE } from '../assets/spriteAssets';

// 16-bit color palette (same as backend)
const COLOR_PALETTE = [
  // Reds
  "#CC0000", "#FF3333", "#FF6666",
  // Oranges
  "#FF6600", "#FF9933", "#FFCC66",
  // Yellows
  "#FFCC00", "#FFFF00", "#FFFF66",
  // Greens
  "#006600", "#4B8B3B", "#66CC66",
  // Cyans
  "#006666", "#00CCCC", "#66FFFF",
  // Blues
  "#000099", "#3333FF", "#6699FF",
  // Purples
  "#660099", "#9933FF", "#CC99FF",
  // Pinks
  "#CC0066", "#FF3399", "#FF99CC",
  // Neutrals
  "#1a1a1a", "#666666", "#CCCCCC", "#F5F5DC",
];

// Skin tone options
const SKIN_TONES = [
  { id: 'light', name: 'Light', color: '#FFDBB4' },
  { id: 'medium', name: 'Medium', color: '#E8B896' },
  { id: 'tan', name: 'Tan', color: '#C68642' },
  { id: 'brown', name: 'Brown', color: '#8D5524' },
  { id: 'dark', name: 'Dark', color: '#5C3317' },
];

// Hair color options (mapped to hair_id for now, could be separate)
const HAIR_STYLES = {
  male: ['Short Crop', 'Slicked Back', 'Messy', 'Buzz Cut'],
  female: ['Ponytail', 'Short Bob', 'Long Straight', 'Braided'],
};

// Flair items
const FLAIR_ITEMS = {
  head: [
    { id: null, name: 'None' },
    { id: 'beer_helmet', name: 'Beer Helmet' },
    { id: 'sunglasses', name: 'Sunglasses' },
  ],
  neck: [
    { id: null, name: 'None' },
    { id: 'gold_chain', name: 'Gold Chain' },
  ],
  feet: [
    { id: null, name: 'None' },
    { id: 'neon_cleats', name: 'Neon Cleats' },
  ],
  held: [
    { id: null, name: 'None' },
    { id: 'oversized_bat', name: 'Oversized Bat' },
  ],
};

export default function AvatarEditor({ 
  initialConfig, 
  onSave, 
  onCancel,
  showCancel = true,
  saveLabel = 'SAVE',
  mode = 'full' // 'full' or 'compact'
}) {
  const [config, setConfig] = useState({
    gender: 'male',
    hair_id: 1,
    primary_color: '#4B8B3B',
    secondary_color: '#F5F5DC',
    skin_tone: 'light',
    equipped_flair: {
      head: null,
      neck: null,
      feet: null,
      held: null,
    },
  });
  
  const [activeTab, setActiveTab] = useState('body');
  const [saving, setSaving] = useState(false);

  // Update config when initialConfig changes
  useEffect(() => {
    if (initialConfig) {
      setConfig(prev => ({
        ...prev,
        gender: initialConfig.gender || prev.gender,
        hair_id: initialConfig.hair_id || prev.hair_id,
        primary_color: initialConfig.primary_color || prev.primary_color,
        secondary_color: initialConfig.secondary_color || prev.secondary_color,
        skin_tone: initialConfig.skin_tone || prev.skin_tone,
        equipped_flair: {
          ...prev.equipped_flair,
          ...(initialConfig.equipped_flair || {}),
        }
      }));
    }
  }, [initialConfig]);

  const handleGenderChange = () => {
    playClick();
    setConfig(prev => ({
      ...prev,
      gender: prev.gender === 'male' ? 'female' : 'male',
      hair_id: 1, // Reset hair when changing gender
    }));
  };

  const handleHairChange = (delta) => {
    playClick();
    setConfig(prev => ({
      ...prev,
      hair_id: ((prev.hair_id - 1 + delta + 4) % 4) + 1,
    }));
  };

  const handleSkinToneChange = (tone) => {
    playClick();
    setConfig(prev => ({
      ...prev,
      skin_tone: tone,
    }));
  };

  const handleColorChange = (type, color) => {
    playClick();
    setConfig(prev => ({
      ...prev,
      [type]: color,
    }));
  };

  const handleFlairChange = (slot, itemId) => {
    playClick();
    setConfig(prev => ({
      ...prev,
      equipped_flair: {
        ...prev.equipped_flair,
        [slot]: itemId,
      },
    }));
  };

  const handleRandomize = () => {
    playClick();
    const randomGender = Math.random() > 0.5 ? 'male' : 'female';
    setConfig({
      gender: randomGender,
      hair_id: Math.floor(Math.random() * 4) + 1,
      primary_color: COLOR_PALETTE[Math.floor(Math.random() * 20)],
      secondary_color: COLOR_PALETTE[Math.floor(Math.random() * COLOR_PALETTE.length)],
      skin_tone: SKIN_TONES[Math.floor(Math.random() * SKIN_TONES.length)].id,
      equipped_flair: {
        head: Math.random() > 0.7 ? FLAIR_ITEMS.head[Math.floor(Math.random() * FLAIR_ITEMS.head.length)].id : null,
        neck: Math.random() > 0.7 ? 'gold_chain' : null,
        feet: Math.random() > 0.7 ? 'neon_cleats' : null,
        held: Math.random() > 0.7 ? 'oversized_bat' : null,
      },
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(config);
    } finally {
      setSaving(false);
    }
  };

  const currentHairStyle = HAIR_STYLES[config.gender][config.hair_id - 1];

  return (
    <div className="avatar-editor" data-testid="avatar-editor">
      {/* Preview */}
      <div className="flex justify-center mb-6">
        <div className="pixel-card p-4 inline-block">
          <AvatarCanvas config={config} size={mode === 'compact' ? 80 : 120} />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-4 justify-center flex-wrap">
        {['body', 'skin', 'colors', 'flair'].map(tab => (
          <button
            key={tab}
            onClick={() => { playClick(); setActiveTab(tab); }}
            className={`px-4 py-2 retro-text text-xs border-2 border-ink transition-colors ${
              activeTab === tab
                ? 'bg-grass text-cream'
                : 'bg-cream text-ink hover:bg-grass/20'
            }`}
            data-testid={`tab-${tab}`}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>

      {/* Body Tab */}
      {activeTab === 'body' && (
        <div className="space-y-4">
          {/* Gender */}
          <div className="pixel-card p-4">
            <label className="retro-text text-xs text-ink block mb-2">STYLE</label>
            <button
              onClick={handleGenderChange}
              className="pixel-btn w-full"
              data-testid="gender-toggle"
            >
              {config.gender === 'male' ? 'MALE' : 'FEMALE'}
            </button>
          </div>

          {/* Hair Style */}
          <div className="pixel-card p-4">
            <label className="retro-text text-xs text-ink block mb-2">HAIR STYLE</label>
            <div className="flex items-center justify-between">
              <button
                onClick={() => handleHairChange(-1)}
                className="pixel-btn p-2"
                data-testid="hair-prev"
              >
                <ChevronLeft size={20} />
              </button>
              <div className="text-center">
                <span className="retro-text text-sm text-ink block">
                  {currentHairStyle}
                </span>
                <span className="retro-body text-xs text-dirt">
                  Style {config.hair_id} of 4
                </span>
              </div>
              <button
                onClick={() => handleHairChange(1)}
                className="pixel-btn p-2"
                data-testid="hair-next"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Skin Tab */}
      {activeTab === 'skin' && (
        <div className="space-y-4">
          <div className="pixel-card p-4">
            <label className="retro-text text-xs text-ink block mb-3">SKIN TONE</label>
            <div className="flex justify-center gap-3">
              {SKIN_TONES.map(tone => (
                <button
                  key={tone.id}
                  onClick={() => handleSkinToneChange(tone.id)}
                  className={`flex flex-col items-center gap-2 p-2 border-2 transition-all ${
                    config.skin_tone === tone.id 
                      ? 'border-ink scale-110 bg-grass/10' 
                      : 'border-transparent hover:scale-105'
                  }`}
                  data-testid={`skin-tone-${tone.id}`}
                >
                  <div 
                    className="w-10 h-10 rounded-full border-2 border-ink"
                    style={{ backgroundColor: tone.color }}
                  />
                  <span className="retro-text text-xs text-ink">{tone.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Colors Tab */}
      {activeTab === 'colors' && (
        <div className="space-y-4">
          {/* Primary Color */}
          <div className="pixel-card p-4">
            <label className="retro-text text-xs text-ink block mb-2">UNIFORM PRIMARY</label>
            <div className="grid grid-cols-7 gap-1">
              {COLOR_PALETTE.map(color => (
                <button
                  key={`primary-${color}`}
                  onClick={() => handleColorChange('primary_color', color)}
                  className={`w-8 h-8 border-2 transition-transform ${
                    config.primary_color === color 
                      ? 'border-ink scale-110' 
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  data-testid={`primary-color-${color}`}
                />
              ))}
            </div>
          </div>

          {/* Secondary Color */}
          <div className="pixel-card p-4">
            <label className="retro-text text-xs text-ink block mb-2">UNIFORM SECONDARY</label>
            <div className="grid grid-cols-7 gap-1">
              {COLOR_PALETTE.map(color => (
                <button
                  key={`secondary-${color}`}
                  onClick={() => handleColorChange('secondary_color', color)}
                  className={`w-8 h-8 border-2 transition-transform ${
                    config.secondary_color === color 
                      ? 'border-ink scale-110' 
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                  data-testid={`secondary-color-${color}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Flair Tab */}
      {activeTab === 'flair' && (
        <div className="space-y-4">
          {Object.entries(FLAIR_ITEMS).map(([slot, items]) => (
            <div key={slot} className="pixel-card p-4">
              <label className="retro-text text-xs text-ink block mb-2">
                {slot.toUpperCase()}
              </label>
              <div className="flex flex-wrap gap-2">
                {items.map(item => (
                  <button
                    key={item.id || 'none'}
                    onClick={() => handleFlairChange(slot, item.id)}
                    className={`px-3 py-2 retro-text text-xs border-2 border-ink transition-colors ${
                      config.equipped_flair[slot] === item.id
                        ? 'bg-grass text-cream'
                        : 'bg-cream text-ink hover:bg-grass/20'
                    }`}
                    data-testid={`flair-${slot}-${item.id || 'none'}`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mt-6">
        <button
          onClick={handleRandomize}
          className="pixel-btn flex-1 flex items-center justify-center gap-2"
          data-testid="randomize-btn"
        >
          <Shuffle size={16} />
          RANDOM
        </button>
        
        {showCancel && onCancel && (
          <button
            onClick={() => { playClick(); onCancel(); }}
            className="pixel-btn flex-1"
            data-testid="cancel-btn"
          >
            CANCEL
          </button>
        )}
        
        <button
          onClick={handleSave}
          disabled={saving}
          className="pixel-btn pixel-btn-primary flex-1 flex items-center justify-center gap-2"
          data-testid="save-avatar-btn"
        >
          <Check size={16} />
          {saving ? 'SAVING...' : saveLabel}
        </button>
      </div>
    </div>
  );
}
